import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fileUrl } from "@/lib/api";

// Source video is portrait (640x1152): full-bleed on phones, sharp framed panel on wide screens.
export default function Hero({ settings }) {
  const [failed, setFailed] = useState("");
  const [ready, setReady] = useState(false);
  const [wide, setWide] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = (e) => setWide(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const video = settings?.hero_video && failed !== settings.hero_video ? settings.hero_video : "";
  const poster = settings?.hero_poster ? fileUrl(settings.hero_poster) : "";

  const media = (testid) => (
    <>
      {poster && <img src={poster} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />}
      {video && (
        <video data-testid={testid} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${ready ? "opacity-100" : "opacity-0"}`}
          src={fileUrl(video)} poster={poster || undefined} preload="auto" autoPlay muted loop playsInline disablePictureInPicture
          onLoadedData={() => setReady(true)} onError={() => setFailed(video)} />
      )}
    </>
  );

  return (
    <section className="relative -mt-16 overflow-hidden bg-black" data-testid="hero-section">
      {poster && <div className="hidden lg:block absolute inset-0 scale-110 blur-3xl opacity-40" style={{ backgroundImage: `url(${poster})`, backgroundSize: "cover", backgroundPosition: "center" }} aria-hidden="true" />}
      <div className="hidden lg:block absolute inset-0 bg-[radial-gradient(ellipse_at_70%_50%,rgba(5,5,5,0.2)_0%,#050505_70%)]" />
      {!wide && (
        <div className="absolute inset-0">
          {media("hero-video-mobile")}
          {!poster && !video && <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_35%,#17171a_0%,#050505_70%)]" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/30 to-black/50" />
        </div>
      )}
      <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 min-h-[88svh] lg:min-h-0 lg:h-[min(92vh,980px)] flex items-end lg:items-center lg:gap-12 xl:gap-20">
        <div className="flex-1 pb-16 sm:pb-20 lg:pb-0 pt-32 lg:pt-16">
          <p className="hidden lg:block eyebrow fade-up text-zinc-300" style={{ animationDelay: "100ms" }}>
            <span className="inline-block w-2 h-2 rounded-full bg-[#ff2a2a] mr-3 align-middle" />
            SPICY3D WORLD
          </p>
          <h1 data-testid="hero-headline" className="font-display font-extrabold uppercase tracking-tighter text-white leading-[0.9] text-5xl sm:text-7xl xl:text-8xl 2xl:text-[7.5rem] mt-6 max-w-3xl fade-up drop-shadow-[0_2px_24px_rgba(0,0,0,0.5)]" style={{ animationDelay: "250ms" }}>
            {settings?.hero_headline || "NOT YOUR AVERAGE PRINT."}
          </h1>
          {settings?.hero_subtext && (
            <p className="mt-6 text-zinc-200 text-base sm:text-lg max-w-lg fade-up" style={{ animationDelay: "400ms" }} data-testid="hero-subtext">{settings.hero_subtext}</p>
          )}
          <div className="mt-10 fade-up" style={{ animationDelay: "550ms" }}>
            <Link to="/shop" className="btn-store group" data-testid="hero-shop-drop-button">
              {settings?.hero_button || "SHOP THE DROP"}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
        {wide && (video || poster) && (
          <div className="relative h-[78%] max-h-[860px] aspect-[640/1152] flex-shrink-0 overflow-hidden border border-white/10 shadow-[0_40px_120px_-20px_rgba(255,42,42,0.25)] fade-up" style={{ animationDelay: "300ms" }} data-testid="hero-video-frame">
            {media("hero-video")}
          </div>
        )}
      </div>
    </section>
  );
}
